import React, { useState, useRef, useEffect } from "react";
import { Typography } from "antd";
import { IFile } from "../../helpers/types";
import playIcon from "../../assets/icons/grey-play.svg";
import pauseIcon from "../../assets/icons/grey-pause.svg";
import { useAudioWaveform } from "../../hooks/useAudioWaveform";
import { useTranslation } from "react-i18next";
import MessageReadStatusIndicator from "../MessageReadStatusIndicator/MessageReadStatusIndicator";
import "./index.less";

// Define a simple audio manager interface
interface SimpleAudioManager {
  resetAllPlayers: () => void;
}

// Add the property to the window object
declare global {
  interface Window {
    globalAudioManager?: SimpleAudioManager;
  }
}

// Simple audio manager - just for resetting all audio players
const createSimpleAudioManager = () => {
  // Keep track of currently playing audio
  let currentlyPlaying: HTMLAudioElement | null = null;

  return {
    // Reset all audio players
    resetAllPlayers: function () {
      if (currentlyPlaying) {
        currentlyPlaying.pause();
        currentlyPlaying = null;
      }
    },
  };
};

// Create a global instance if it doesn't exist
if (!window.globalAudioManager) {
  window.globalAudioManager = createSimpleAudioManager();
}

// We'll use window.globalAudioManager directly when needed

interface AudioMessageProps {
  audioFile: IFile;
  duration?: number;
  isOwn?: boolean;
  className?: string;
  date: string;
  isProcessing?: boolean;
  isRead?: boolean;
}

const AudioMessageComponent: React.FC<AudioMessageProps> = ({
  audioFile,
  duration = 0,
  isOwn = false,
  className = "",
  date,
  isProcessing = false,
  isRead = false,
}) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration);
  const [isLoading, setIsLoading] = useState(true);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [audioError, setAudioError] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressRef = useRef<HTMLDivElement | null>(null);
  const audioUrl = audioFile.absolutePath || audioFile.path || "";

  // Use real audio waveform analysis
  const { waveformData, isLoading: isAnalyzing } = useAudioWaveform(audioUrl, {
    segments: 40, // fewer bars to better fit container
    scaleFactor: 16,
    fallbackToSimple: true,
  });

  // Show processing state ONLY for this specific audio message
  // This ensures we show the processing state only for the uploading audio
  // Each audio player is completely independent
  const showProcessingState: boolean = isProcessing || (!audioUrl && !!audioFile._id);

  // Track mounted state to prevent state updates after unmount
  const isMounted = useRef(true);

  // Force processing state to true if isProcessing is explicitly set
  // This ensures we always show the processing state when uploading
  useEffect(() => {
    if (isProcessing) {
      setIsLoading(false);
      setAudioLoaded(false);
    }
  }, [isProcessing]);

  // Set up mounted state tracking
  useEffect(() => {
    isMounted.current = true;

    return () => {
      // Mark as unmounted to prevent state updates
      isMounted.current = false;
    };
  }, []);
  const audioId = audioFile._id || "audio-" + Date.now();

  // Create audio element manually to better control its lifecycle
  useEffect(() => {
    // Skip audio loading ONLY for this specific audio if it's processing or has no URL
    // This ensures other audio players can still load and play
    if (isProcessing || !audioUrl) {
      setIsLoading(false);
      // Make sure we're not showing the loading state for processing messages
      if (isProcessing) {
        setAudioLoaded(false);
      }
      return () => {}; // Empty cleanup function when processing
    }

    // Only proceed with audio loading for non-processing messages with valid URLs

    // Reset state for new audio
    setCurrentTime(0);
    setIsPlaying(false);
    setIsLoading(true);
    setAudioLoaded(false);
    setAudioError(false);

    // Create a new audio element with error handling
    let audioElement: HTMLAudioElement;

    try {
      audioElement = new Audio(audioUrl);
      audioElement.preload = "metadata";
      audioRef.current = audioElement;

      // Set up event listeners with mounted state check
      const handleLoadedMetadata = () => {
        if (isMounted.current) {
          setAudioDuration(audioElement.duration);
          setIsLoading(false);
          setAudioLoaded(true);
        }
      };

      const handleTimeUpdate = () => {
        if (isMounted.current) {
          setCurrentTime(audioElement.currentTime);
        }
      };

      const handleEnded = () => {
        if (isMounted.current) {
          setIsPlaying(false);
          setCurrentTime(0);
        }
      };

      const handleCanPlay = () => {
        if (isMounted.current) {
          setIsLoading(false);
          setAudioLoaded(true);
        }
      };

      const handleError = () => {
        console.error("Error loading audio:", audioUrl);
        if (isMounted.current) {
          setIsLoading(false);
          setAudioError(true);
        }
      };

      audioElement.addEventListener("loadedmetadata", handleLoadedMetadata);
      audioElement.addEventListener("timeupdate", handleTimeUpdate);
      audioElement.addEventListener("ended", handleEnded);
      audioElement.addEventListener("canplay", handleCanPlay);
      audioElement.addEventListener("error", handleError);

      // No timeouts - they cause issues with audio players

      // Clean up - simplified to avoid issues
      return () => {
        // Pause audio element
        if (audioElement) {
          audioElement.pause();

          // Remove all event listeners
          audioElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
          audioElement.removeEventListener("timeupdate", handleTimeUpdate);
          audioElement.removeEventListener("ended", handleEnded);
          audioElement.removeEventListener("canplay", handleCanPlay);
          audioElement.removeEventListener("error", handleError);

          // Release memory
          if (audioElement.src.startsWith("blob:")) {
            URL.revokeObjectURL(audioElement.src);
          }
        }
      };
    } catch (error: any) {
      setIsLoading(false);
      setAudioError(true);
      return () => {};
    }
  }, [audioUrl, audioId, isProcessing, showProcessingState]);

  const togglePlayPause = () => {
    // Only disable THIS audio player if it's processing
    // Other audio players should remain functional
    if (showProcessingState) {
      return;
    }

    const audio = audioRef.current;
    if (!audio || isLoading) {
      return;
    }

    if (isPlaying) {
      // Pause this specific audio without affecting others
      audio.pause();
      if (isMounted.current) {
        setIsPlaying(false);
      }
    } else {
      // Reset to beginning if at the end
      if (audio.currentTime >= audio.duration) {
        audio.currentTime = 0;
      }

      // Ensure audio is properly loaded before playing
      if (!audioLoaded) {
        audio.preload = "auto";
        // Set a small delay to allow loading to start
        setTimeout(() => {
          if (isMounted.current) {
            playAudio(audio);
          }
        }, 100);
      } else {
        playAudio(audio);
      }
    }
  };

  // Helper function to play audio
  const playAudio = (audio: HTMLAudioElement) => {
    // Play this specific audio directly without using the manager
    // This avoids TypeScript errors and potential issues
    audio.play().catch((error: Error) => {
      console.error("Error playing audio:", error);
      if (isMounted.current) {
        setIsPlaying(false);
      }
    });

    if (isMounted.current) {
      setIsPlaying(true);
    }
  };

  const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Only disable THIS progress bar if this specific audio is processing
    if (showProcessingState) {
      return;
    }

    const audio = audioRef.current;
    const progressBar = progressRef.current;
    if (!audio || !progressBar || !audioDuration || isLoading) {
      return;
    }

    try {
      const rect = progressBar.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const progressWidth = rect.width;
      const clickRatio = Math.max(0, Math.min(1, clickX / progressWidth)); // Ensure ratio is between 0 and 1
      const newTime = clickRatio * audioDuration;

      // Ensure the time is valid
      if (isNaN(newTime) || newTime < 0 || newTime > audioDuration) {
        console.warn("Invalid seek time calculated:", newTime);
        return;
      }

      audio.currentTime = newTime;
      if (isMounted.current) {
        setCurrentTime(newTime);
      }

      // If not already playing and audio is loaded, start playback
      if (!isPlaying && audioLoaded) {
        playAudio(audio);
      }
    } catch (error) {
      console.error("Error during progress click handling:", error);
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressPercent = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  return (
    <div className={`audio-message-container ${isOwn ? "own-message" : "other-message"} ${className} ${showProcessingState ? "processing" : ""}`}>
      <div className="audio-message-content">
        {(() => {
          const isDisabled = showProcessingState || isLoading || audioError;
          return (
            <div
              role="button"
              tabIndex={0}
              aria-disabled={isDisabled}
              className="play-pause-button"
              onClick={isDisabled ? undefined : togglePlayPause}
              onKeyDown={(e) => {
                if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  togglePlayPause();
                }
              }}
              style={{ pointerEvents: isDisabled ? "none" : "auto" }}
            >
              {showProcessingState ? (
                <div className="audio-loading-spinner" />
              ) : isLoading ? (
                <div className="audio-loading-spinner" />
              ) : isPlaying ? (
                <img src={pauseIcon} alt="pause" className="play-pause-icon" />
              ) : (
                <img src={playIcon} alt="play" className="play-pause-icon" />
              )}
            </div>
          );
        })()}

        <div className="audio-info">
          <div className="audio-progress-container">
            <div className="audio-progress-row">
              <div ref={progressRef} className="audio-waveform" onClick={handleProgressClick}>
                {waveformData.map((height, index) => {
                  const isPlayed = !showProcessingState && index < (progressPercent / 100) * waveformData.length;
                  const barHeight = showProcessingState ? Math.random() * 16 + 6 : height * 20 + 4;

                  return (
                    <div
                      key={index}
                      className={`waveform-bar ${showProcessingState ? "processing-bar" : ""} ${isPlayed ? "played" : ""} ${isAnalyzing ? "analyzing" : ""}`}
                      style={{
                        height: `${barHeight}px`,
                        animationDelay: showProcessingState ? `${index * 0.05}s` : `${index * 0.1}s`,
                        opacity: isAnalyzing ? 0.6 : 1,
                      }}
                    />
                  );
                })}
              </div>
              {showProcessingState ? (
                <Typography.Text className="duration-right processing-text">{t("audio-uploading")}</Typography.Text>
              ) : (
                <Typography.Text className="duration-right">{formatTime(audioDuration)}</Typography.Text>
              )}
            </div>
          </div>

          <div className="audio-time-info">
            <Typography.Text className="message-time">{date}</Typography.Text>
            {isOwn && <MessageReadStatusIndicator isRead={isRead} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioMessageComponent;
